/** @jsxImportSource @emotion/react */
import { OutputData } from "@editorjs/editorjs";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { css, Dialog, DialogActions, IconButton } from "@mui/material";
import { DateTime } from "luxon";
import { VFC } from "react";
import { useTranslation } from "react-i18next";
import { CountryPath } from "src/entities/CountryPath";
import getCountryName from "src/utils/getCountryName";
import {
  getCountryTimezone,
  getCountryTimezoneAbbreviation,
} from "src/utils/getCountryTimezone";
import Button from "../common/Button";

interface Props {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  title: string;
  outputBody?: OutputData;
  country: CountryPath;
  isScheduled: boolean;
  scheduledDate?: DateTime;
}

const FinalConfirmationModal: VFC<Props> = (props: Props) => {
  const { t } = useTranslation();

  // Process HTML to add target="_blank" to all links
  const processHtmlWithLinks = (html: string) => {
    return html.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
  };

  const renderContent = () => {
    if (!props.outputBody) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return props.outputBody.blocks.map((block: any) => {
      const blockKey = `${block.type}-${
        block.data.text?.substring(0, 20) ||
        block.data.caption?.substring(0, 20) ||
        Math.random()
      }`;

      switch (block.type) {
        case "header": {
          const { level } = block.data;
          const HeaderTag = `h${level}` as keyof JSX.IntrinsicElements;
          const headerStyles = {
            1: {
              fontSize: "20px",
              fontWeight: 700,
              marginTop: "16px",
              marginBottom: "12px",
            },
            2: {
              fontSize: "18px",
              fontWeight: 600,
              marginTop: "14px",
              marginBottom: "10px",
            },
            3: {
              fontSize: "16px",
              fontWeight: 600,
              marginTop: "12px",
              marginBottom: "8px",
            },
            4: {
              fontSize: "14px",
              fontWeight: 600,
              marginTop: "10px",
              marginBottom: "6px",
            },
            5: {
              fontSize: "13px",
              fontWeight: 600,
              marginTop: "8px",
              marginBottom: "6px",
            },
            6: {
              fontSize: "12px",
              fontWeight: 600,
              marginTop: "8px",
              marginBottom: "6px",
            },
          };
          return (
            <HeaderTag
              key={blockKey}
              style={headerStyles[level] || headerStyles[3]}
            >
              {block.data.text}
            </HeaderTag>
          );
        }
        case "paragraph": {
          return (
            <p
              key={blockKey}
              style={{
                fontSize: "13px",
                lineHeight: "1.6",
                marginBottom: "10px",
              }}
              css={paragraphStyle}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: processHtmlWithLinks(block.data.text),
              }}
            />
          );
        }
        case "list": {
          return (
            <ul
              key={blockKey}
              style={{
                fontSize: "13px",
                marginBottom: "10px",
                paddingLeft: "20px",
              }}
            >
              {block.data.items.map((item: string) => (
                <li
                  key={`${blockKey}-${item.substring(0, 20)}-${Math.random()}`}
                  style={{ marginBottom: "4px" }}
                  css={paragraphStyle}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: processHtmlWithLinks(item),
                  }}
                />
              ))}
            </ul>
          );
        }
        case "image": {
          return (
            <div
              key={blockKey}
              style={{ marginBottom: "12px", textAlign: "center" }}
            >
              <img
                src={block.data.file.url}
                alt={block.data.caption || ""}
                style={{ maxWidth: "80%", height: "auto" }}
              />
              {block.data.caption && (
                <p
                  style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}
                >
                  {block.data.caption}
                </p>
              )}
            </div>
          );
        }
        case "quote": {
          return (
            <blockquote key={blockKey} css={quoteStyle}>
              <p
                style={{ fontSize: "13px", fontStyle: "italic" }}
                css={paragraphStyle}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: processHtmlWithLinks(block.data.text),
                }}
              />
              {block.data.caption && (
                <cite
                  style={{
                    fontSize: "11px",
                    color: "#666",
                    marginTop: "6px",
                    display: "block",
                  }}
                >
                  — {block.data.caption}
                </cite>
              )}
            </blockquote>
          );
        }
        default:
          return null;
      }
    });
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <div css={modalContainer}>
        <div css={headerSection}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {t("final_confirmation.title")}
            </h2>
            <IconButton onClick={props.handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <p className="text-gray-600 mt-2">
            {t("final_confirmation.description")}
          </p>
        </div>

        <div css={contentSection}>
          <div css={articleInfo}>
            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <span className="text-xs text-gray-500">
                  {t("final_confirmation.target_country")}:
                </span>
                <span className="ml-2 text-sm font-semibold">
                  {getCountryName(props.country)}
                </span>
              </div>
              {props.isScheduled && props.scheduledDate && (
                <div className="flex items-center">
                  <span className="text-xs text-gray-500">
                    {t("final_confirmation.scheduled_time")}:
                  </span>
                  <span className="ml-2 text-sm font-semibold">
                    {props.scheduledDate
                      .setZone(getCountryTimezone(props.country))
                      .toFormat("MM/dd/yyyy hh:mm a")}{" "}
                    ({getCountryTimezoneAbbreviation(props.country)})
                  </span>
                </div>
              )}
            </div>
          </div>

          <div css={articleContent}>
            <h3 className="text-lg font-bold mb-3">{props.title}</h3>
            <div css={contentScroll}>{renderContent()}</div>
          </div>

          <div css={warningSection}>
            <div className="flex items-start">
              <WarningAmberIcon
                className="text-yellow-700 mr-2 mt-1"
                style={{ fontSize: "20px" }}
              />
              <div className="flex-1">
                <p className="font-semibold mb-1 text-sm">
                  {t("final_confirmation.please_check")}
                </p>
                <ul
                  className="list-disc list-inside space-y-1"
                  style={{ fontSize: "12px" }}
                >
                  <li>{t("final_confirmation.check_country")}</li>
                  <li>{t("final_confirmation.check_content")}</li>
                  <li>{t("final_confirmation.check_links")}</li>
                  <li>{t("final_confirmation.cannot_edit")}</li>
                  <li>
                    {t("final_confirmation.prohibited_content")}{" "}
                    <a
                      href="https://drive.google.com/file/d/1bUzzT7cD969seAcGdY0wgM-u8MBAIJJ4/view"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      {t("final_confirmation.terms_conditions")}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogActions css={actionSection}>
          <Button
            text={t("final_confirmation.cancel")}
            variant="outlined"
            onClick={props.handleClose}
          />
          <Button
            text={t("final_confirmation.execute_delivery")}
            onClick={props.handleConfirm}
          />
        </DialogActions>
      </div>
    </Dialog>
  );
};

const modalContainer = css`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const headerSection = css`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const contentSection = css`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const articleInfo = css`
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const articleContent = css`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const contentScroll = css`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
`;

const warningSection = css`
  margin-top: 12px;
  padding: 12px;
  background-color: #fff8dc;
  border-radius: 6px;
  border: 1px solid #ffd700;
`;

const actionSection = css`
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
`;

const quoteStyle = css`
  margin: 12px 0;
  padding-left: 12px;
  border-left: 3px solid #e0e0e0;
`;

const paragraphStyle = css`
  a {
    color: #1976d2;
    text-decoration: underline;
    &:hover {
      color: #1565c0;
    }
    &:after {
      content: "↗";
      font-size: 0.8em;
      margin-left: 2px;
      vertical-align: super;
    }
  }
`;

export default FinalConfirmationModal;
